import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, LabelList } from 'recharts';

const PerformanceChart = ({ performanceData }) => {
  if (!performanceData || !performanceData.sales_tons || !performanceData.dsp_sales) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexDirection: 'column', gap: 1}}>
        <Typography variant="h2">
          Performance History
        </Typography>
        <Typography variant="body2" color="#666">
          No data available
        </Typography>
      </Box>
    );
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const salesData = performanceData.sales_tons.slice(0, 12);
  const dspData = performanceData.dsp_sales.slice(0, 12);
  const chartData = salesData.map((total, index) => {
    const dsp = dspData[index] || 0;
    const other = Math.max(0, total - dsp);   
    return {
      month: months[index],
      otherProducts: other,
      dspSales: dsp,
      total: total
    };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h2">
          Performance History
        </Typography>
        
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              bgcolor: '#B1D3DB', 
              borderRadius: '50%' 
            }} />
            <Typography variant="p3" sx={{ color: '#666' }}>
              Other products
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              bgcolor: '#FFA525', 
              borderRadius: '50%'
            }} />
            <Typography variant="p3" sx={{ color: '#666' }}>
              DSP Sales
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection:'column', gap: 2, }}>
        <Typography variant="labelD" sx={{ color: '#666', borderBottom: '1px solid #D9D9D9', pb:0.5 }}>
          Sales (Ton)
        </Typography>

        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={chartData}
            margin={{ top: 0, right: 0, bottom: 0, left: -25, }}
          >
            <CartesianGrid strokeDasharray="4 4" stroke="#d9d9d9" horizontal={true} vertical={false} />
            <XAxis 
              dataKey="month"
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={false}
            />
            <YAxis 
              axisLine={false}
              // domain={[0, 100]}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              // ticks={[0, 20, 40, 60, 80, 100]}
            />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              coordinate={{ x: 0, y: 0 }}
              allowEscapeViewBox={{ x: false, y: true }}
              wrapperStyle={{ zIndex: 1000 }}
              content={({ active, payload, label, coordinate }) => {
                  if (active && payload && payload.length && coordinate) {
                  // Check if any bar has a value greater than 0
                  const hasValue = payload.some(entry => entry.value > 0);
                  if (!hasValue || !label) return null;
                  const total = payload[0].payload.total || payload.reduce((sum, entry) => sum + entry.value, 0);
                  
                  // Calculate tooltip positioning to prevent overflow
                  const tooltipWidth = 100; // Approximate width of tooltip
                  const chartWidth = 400; // Approximate chart width
                  const isNearRightEdge = coordinate.x > chartWidth - tooltipWidth;
                  const isNearLeftEdge = coordinate.x < tooltipWidth / 2;
                  
                  let transformValue = 'translateX(-50%)';
                  if (isNearRightEdge) {
                    transformValue = 'translateX(-100%)';
                  } else if (isNearLeftEdge) {
                    transformValue = 'translateX(0%)';
                  }
                  
                  return (
                      <Box
                          style={{
                              backgroundColor: '#8C8C8C',
                              color: '#fff',
                              fontSize: '0.75rem',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
                              position: 'absolute',
                              left: coordinate.x,
                              top: coordinate.y - total * 0.2 - 30,
                              zIndex: 1001,
                              width: 'max-content',
                              maxWidth: '180px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              transform: transformValue
                              
                          }}>
                          {`Total: ${Number(total).toFixed(2)} Ton`}
                      </Box>
                  );
                  }
                  return null;
              }}
            />
            
            {/* <CustomTooltip   /> */}
            <Bar 
              dataKey="otherProducts" 
              stackId="a" 
              fill="#B1D3DB"
              radius={[0, 0, 0, 0]}
              barSize={8}
            >
            
            </Bar>
            <Bar 
              dataKey="dspSales" 
              stackId="a" 
              fill="#FFA525"
              radius={[4, 4, 0, 0]}
              barSize={8}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PerformanceChart;